import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  ArchiveRestore,
  Bell,
  BellRing,
  ChevronRight,
  ClipboardList,
  DollarSign,
  Eye,
  Inbox,
  List,
  ListChecks,
  Palette,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Settings hub card grid (T-0110 / E10 wave 6).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-firm.js →
 * window.Screens.firmSettings` (the "Override the hub" block at the bottom
 * of the file). Eight cards, 2-column grid on wide viewports, single column
 * on narrow. The "Methodology library" card is gone (C2-03): ADR-022
 * decision 5 retired the methodology-template library, and
 * `/settings/library` now lands on Assessment defaults.
 *
 * Each card is a `<Link>` whose target is the corresponding sub-route under
 * `/settings/*`. The card renders:
 *   - a tinted-paper icon tile (32px) with a lucide icon — README §4
 *     mentions the icons by hint (Users / Eye / Library / Inbox / Bell /
 *     BellRing / DollarSign / Palette).
 *   - the section title (h3). The prototype's feature-spec code chip
 *     ("E01-F07") is not rendered: it is the backlog's vocabulary, not the
 *     reader's (C2-32).
 *   - a one-line description below the title.
 *   - a meta footer with the secondary count and an "Open" hint with the
 *     chevron icon.
 *
 * The contents (count strings, icon choices, description copy) are kept
 * static here — T-0110 does not block on the per-section list endpoints
 * being live for every section. Counts are placeholder "—" by default;
 * the users + viewers sections show live counts derived from the props.
 */

export interface HubCardCounts {
  /** Number of internal users; from `useFirmUsersQuery`. */
  usersTotal?: number;
  /** Active users, for the meta line. */
  usersActive?: number;
  /** Active viewer grants; from `useClientViewerGrantsQuery`. */
  viewersActive?: number;
  /** Pending viewer invitations. */
  viewersPending?: number;
  /** Expired viewer grants (treated as `status === 'revoked'` because the
   *  schema doesn't yet model an explicit expiry column). */
  viewersExpired?: number;
}

interface HubCard {
  to: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Feature-spec code, for tracing a card to its spec in code only; never rendered. */
  code: string;
  /** Secondary meta string (live counts where available). */
  meta: (counts: HubCardCounts) => string;
  /** T-0161: shown only to callers who may delete engagements (firm admins). */
  requiresCanDeleteEngagements?: boolean;
}

const CARDS: ReadonlyArray<HubCard> = [
  {
    to: '/settings/users',
    title: 'Users & roles',
    // MFA is Entra policy, not a Caliber setting (T-0227 / C2-20).
    description: 'Provision internal staff and assign roles. Sign-in is through Microsoft Entra.',
    icon: Users,
    code: 'E01-F07',
    meta: (c) =>
      c.usersTotal !== undefined
        ? `${c.usersTotal} user${c.usersTotal === 1 ? '' : 's'}` +
          (c.usersActive !== undefined ? ` · ${c.usersActive} active` : '')
        : 'Provision and manage staff',
  },
  {
    to: '/settings/viewers',
    title: 'External client viewers',
    description: 'Scoped read-only access for client stakeholders.',
    icon: Eye,
    code: 'E01-F06',
    meta: (c) =>
      c.viewersActive !== undefined
        ? `${c.viewersActive} active · ${c.viewersPending ?? 0} pending` +
          (c.viewersExpired !== undefined && c.viewersExpired > 0
            ? ` · ${c.viewersExpired} expired`
            : '')
        : 'Magic-link or SSO',
  },
  {
    to: '/settings/assessment-defaults',
    title: 'Assessment defaults',
    description:
      'Health domains, readiness criteria with weights and thresholds, finding types and the scoring scale every new engagement starts with.',
    icon: ClipboardList,
    code: 'E20-F01',
    meta: () => 'Domains · criteria · finding types · scale',
  },
  {
    to: '/settings/requirement-library',
    title: 'Requirement library',
    description: 'Firm-standard requirements by module: browse, search, curate, publish.',
    icon: ListChecks,
    code: 'E11-F01 / F02',
    meta: () => 'Modules · versions · sources',
  },
  {
    to: '/settings/discovery-frameworks',
    title: 'Discovery frameworks',
    description: 'The discovery method: areas, interview types, questionnaires and rules.',
    icon: List,
    code: 'E12-F01',
    meta: () => 'Draft · publish · history',
  },
  {
    to: '/settings/queue',
    title: 'Promote-back queue',
    description: 'Engagement-level edits proposed for firm-wide promotion.',
    icon: Inbox,
    code: 'E01-F08',
    meta: () => 'Methodology change requests',
  },
  {
    to: '/settings/notifications',
    title: 'Notification policies',
    description: 'Firm-wide event defaults; in-app, email, digest channels.',
    icon: Bell,
    code: 'E09-F04',
    meta: () => 'Firm-wide defaults',
  },
  {
    to: '/settings/my-notifications',
    title: 'My notifications',
    description: 'Personal channel overrides + per-engagement mute toggles.',
    icon: BellRing,
    code: 'E09-F04',
    meta: () => 'Personal preferences',
  },
  {
    to: '/settings/ai-costs',
    title: 'AI cost governance',
    description: 'Firm budget caps, per-engagement spend, model pinning.',
    icon: DollarSign,
    code: 'T-0014',
    meta: () => 'Cap meters · per-engagement spend',
  },
  {
    to: '/settings/brand',
    title: 'Brand & report templates',
    description: 'PDF cover pages, headers, color tokens for exports.',
    icon: Palette,
    code: 'E05-F02',
    meta: () => 'Cover · header · footer',
  },
  {
    to: '/settings/deleted-engagements',
    title: 'Deleted engagements',
    description: 'Engagements a firm admin deleted: who, when, and restore.',
    icon: ArchiveRestore,
    code: 'T-0161',
    meta: () => 'Firm admins only',
    requiresCanDeleteEngagements: true,
  },
];

export interface HubCardGridProps {
  counts?: HubCardCounts;
  /** From `GET /api/v1/me`; false hides the firm-admin-only cards. */
  canDeleteEngagements?: boolean;
}

export function HubCardGrid({ counts = {}, canDeleteEngagements = false }: HubCardGridProps) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {CARDS.filter((card) => !card.requiresCanDeleteEngagements || canDeleteEngagements).map(
        (card) => (
          <HubCardLink key={card.to} card={card} counts={counts} />
        ),
      )}
    </div>
  );
}

function HubCardLink({ card, counts }: { card: HubCard; counts: HubCardCounts }) {
  const Icon = card.icon;
  return (
    <Link
      to={card.to}
      className="group flex flex-col gap-2 rounded-card border border-ink-100 bg-paper-0 p-card-pad text-ink-900 no-underline shadow-sm transition-colors hover:border-ink-200 hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
    >
      <div className="flex items-start gap-2.5">
        <span
          aria-hidden="true"
          className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-card bg-paper-100 text-ink-700"
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="flex flex-1 flex-col">
          <h3 className="text-h3 font-semibold text-ink-900">{card.title}</h3>
          <p className="mt-1 text-meta leading-[18px] text-ink-500">{card.description}</p>
        </div>
      </div>
      <div className="mt-auto flex items-center justify-between pt-1 text-meta text-ink-500">
        <span>{card.meta(counts)}</span>
        <span className="inline-flex items-center gap-1 text-ink-700 group-hover:text-accent-amber-700">
          Open
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}
